import { useState, useEffect, useRef } from "react";

/**
 * useState that persists to localStorage under `key`.
 *
 * Behaves like useState otherwise. Writes are debounced into the next paint
 * via useEffect, so consumers don't need to know about storage.
 *
 * Usage:
 *   const [status, setStatus] = usePersistentState("items:status", "all");
 *
 * To reset back to the initial value, call setValue(initial) — this also
 * overwrites localStorage so the next mount starts fresh.
 */
export default function usePersistentState(key, initial) {
  const [value, setValue] = useState(() => {
    try {
      const raw = window.localStorage.getItem(key);
      if (raw === null) return initial;
      return JSON.parse(raw);
    } catch {
      return initial;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch { /* storage full / disabled */ }
  }, [key, value]);

  return [value, setValue];
}

/**
 * Variant for Set values — serializes as array in storage and hydrates back
 * into a Set. The exposed state is a Set so existing .has/.size/.add code
 * keeps working.
 */
export function usePersistentSet(key, initialArray = []) {
  const [arr, setArr] = usePersistentState(key, initialArray);
  const setRef = useRef(new Set(arr));
  // Keep the Set in sync whenever the persisted array changes.
  if (setRef.current.size !== arr.length || arr.some((v) => !setRef.current.has(v))) {
    setRef.current = new Set(arr);
  }
  const setSet = (updater) => {
    setArr((prev) => {
      const prevSet = new Set(prev);
      const next = typeof updater === "function" ? updater(prevSet) : updater;
      return Array.from(next);
    });
  };
  return [setRef.current, setSet];
}
